import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/UserManagement`;

// ============================================================
// Servicio de Gestión de Usuarios y Roles
// Controller: UserManagementController
// Ruta base: /api/UserManagement
// Autenticación: JWT Bearer (todos los endpoints)
// Nota: adminId e ipAddress se extraen automáticamente en el backend
// ============================================================

/**
 * Crear cuenta de usuario para un empleado existente.
 * El backend genera el hash/salt de la contraseña temporal con HMACSHA512.
 * El usuario creado tendrá BIT_FORCE_PASSWORD_CHANGE = 1 (cambio obligatorio en primer login).
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del empleado al que se le crea la cuenta
 * @param {string} data.mail - Correo electrónico (login del usuario)
 * @param {string} data.tempPassword - Contraseña temporal (el backend genera hash/salt)
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Usuario creado exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Empleado no encontrado o inactivo" }
 * { "result": 2, "message": "El empleado ya tiene cuenta de usuario" }
 * { "result": 3, "message": "El correo electrónico es requerido" }
 * { "result": 4, "message": "El correo electrónico ya está en uso" }
 * { "result": 5, "message": "Hash y salt de contraseña son requeridos" }
 */
export const createUser = async ({ employeeId, mail, tempPassword }) => {
  try {
    const response = await axios.post(
      `${API_URL}/create-user`,
      { employeeId, mail, tempPassword },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en createUser:", error);
    throw error;
  }
};

/**
 * Desactivar cuenta de usuario.
 * Si el usuario tiene sesión activa, se cierra automáticamente (razón: admin_forced_logout).
 * El admin NO puede desactivar su propia cuenta.
 *
 * @param {number} employeeId - ID del usuario a desactivar
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Usuario desactivado exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Usuario no encontrado" }
 * { "result": 2, "message": "El usuario ya está inactivo" }
 * { "result": 3, "message": "No puede desactivar su propia cuenta" }
 */
export const deactivateUser = async (employeeId) => {
  try {
    const response = await axios.put(
      `${API_URL}/deactivate-user`,
      { employeeId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en deactivateUser:", error);
    throw error;
  }
};

/**
 * Reactivar una cuenta de usuario previamente desactivada.
 * Fuerza cambio de contraseña por seguridad HIPAA.
 * Resetea: NUM_STATUS=1, BIT_FORCE_PASSWORD_CHANGE=1, NUM_FAILED_ATTEMPTS=0,
 *          BIT_LOCKED=0, DAT_LOCKED_UNTIL=NULL
 *
 * @param {number} employeeId - ID del usuario a reactivar
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Usuario reactivado exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Usuario no encontrado" }
 * { "result": 2, "message": "El usuario ya está activo" }
 */
export const reactivateUser = async (employeeId) => {
  try {
    const response = await axios.put(
      `${API_URL}/reactivate-user`,
      { employeeId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en reactivateUser:", error);
    throw error;
  }
};

/**
 * Asignar un rol a un usuario existente.
 * Si el rol fue revocado previamente, lo reactiva en lugar de insertar nuevo registro.
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del usuario al que se asigna el rol
 * @param {number} data.roleId - ID del rol a asignar
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Rol asignado exitosamente" }
 * -- o si es reasignación de un rol previamente revocado:
 * { "result": 0, "message": "Rol reasignado exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Usuario no encontrado o inactivo" }
 * { "result": 2, "message": "Rol no encontrado o inactivo" }
 * { "result": 3, "message": "El usuario ya tiene este rol asignado" }
 */
export const assignRole = async ({ employeeId, roleId }) => {
  try {
    const response = await axios.post(
      `${API_URL}/assign-role`,
      { employeeId, roleId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en assignRole:", error);
    throw error;
  }
};

/**
 * Revocar un rol de un usuario existente.
 * No permite revocar el último rol activo del usuario.
 * El rol no se elimina, solo se desactiva (NUM_STATUS = 0).
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del usuario al que se revoca el rol
 * @param {number} data.roleId - ID del rol a revocar
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Rol revocado exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Usuario no encontrado" }
 * { "result": 2, "message": "El usuario no tiene este rol asignado" }
 * { "result": 3, "message": "No puede revocar el último rol del usuario" }
 */
export const revokeRole = async ({ employeeId, roleId }) => {
  try {
    const response = await axios.put(
      `${API_URL}/revoke-role`,
      { employeeId, roleId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en revokeRole:", error);
    throw error;
  }
};

/**
 * Desbloquear manualmente una cuenta de usuario bloqueada por intentos fallidos.
 * Solo funciona con usuarios activos (NUM_STATUS=1) que están bloqueados (BIT_LOCKED=1).
 * Resetea: BIT_LOCKED=0, NUM_FAILED_ATTEMPTS=0, DAT_LOCKED_UNTIL=NULL
 *
 * @param {number} employeeId - ID del usuario a desbloquear
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Cuenta desbloqueada exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Usuario no encontrado" }
 * { "result": 2, "message": "El usuario está inactivo, use reactivar" }
 * { "result": 3, "message": "La cuenta no está bloqueada" }
 */
export const unlockAccount = async (employeeId) => {
  try {
    const response = await axios.put(
      `${API_URL}/unlock-account`,
      { employeeId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en unlockAccount:", error);
    throw error;
  }
};

/**
 * Resetear la contraseña de un usuario (acción de admin).
 * El backend genera el hash/salt de la contraseña temporal con HMACSHA512.
 * El usuario reseteado tendrá BIT_FORCE_PASSWORD_CHANGE = 1.
 * La nueva contraseña expira en 90 días.
 * El admin NO puede resetear su propia contraseña.
 *
 * @param {Object} data
 * @param {number} data.employeeId - ID del usuario al que se resetea la contraseña
 * @param {string} data.tempPassword - Nueva contraseña temporal (el backend genera hash/salt)
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Contraseña reseteada exitosamente" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Usuario no encontrado" }
 * { "result": 2, "message": "El usuario está inactivo" }
 * { "result": 3, "message": "No puede resetear su propia contraseña" }
 * { "result": 4, "message": "Hash y salt de contraseña son requeridos" }
 */
export const adminResetPassword = async ({ employeeId, tempPassword }) => {
  try {
    const response = await axios.put(
      `${API_URL}/admin-reset-password`,
      { employeeId, tempPassword },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en adminResetPassword:", error);
    throw error;
  }
};

/**
 * Forzar cierre de sesión de un usuario (acción de admin).
 * El adminId se extrae del JWT automáticamente en el backend.
 * El usuario afectado NO recibe notificación inmediata; en su próximo request,
 * validate-session retornará error y el frontend lo redirigirá a login.
 *
 * @param {Object} data
 * @param {number} data.targetEmployeeId - ID del empleado cuya sesión se cerrará
 * @param {string} [data.reason] - Razón del cierre (queda en auditoría)
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Session closed successfully for user: John Doe" }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Administrator user not found or inactive" }
 * { "result": 2, "message": "Target user not found" }
 * { "result": 3, "message": "Target user does not have an active session" }
 */
export const adminForceLogout = async ({ targetEmployeeId, reason }) => {
  try {
    const response = await axios.put(
      `${API_URL}/admin-force-logout`,
      { targetEmployeeId, reason },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error en adminForceLogout:", error);
    throw error;
  }
};
